<!--
  FilterEditorModal.vue — Modal host for FilterConditionsEditor.

  Edits a SerializedFilter in a self-contained useFilter instance: reset
  and reloaded on each open, never mutates the parent value directly.
  Save emits the new serialized value; Cancel emits close only.
-->
<template>
  <ModalContainer :show="isOpen" flush @close="handleCancel">
    <div class="modal-card filter-editor-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ title }}
          <span v-if="targetType" class="text-muted text-6 font-normal"> · {{ targetType }} </span>
        </p>
        <button class="delete" aria-label="close" @click="handleCancel"></button>
      </header>

      <section class="modal-card-body">
        <!-- No target type configured -->
        <div v-if="!targetType" class="notification notification-warning text-7">
          This filter field has no <code>subtype</code> set. Configure the schema to point at a
          record type (e.g. <code>deal</code>) before you can define conditions.
        </div>

        <!-- No fields available on that type -->
        <div v-else-if="!fields.length" class="notification text-7 text-muted">
          No fields found for type <strong>{{ targetType }}</strong
          >. Make sure the types store has loaded, or that the target schema has at least one field
          defined.
        </div>

        <FilterConditionsEditor
          v-else
          :filters="filters"
          :fields="fields"
          :record-type="targetType"
        />
      </section>

      <footer class="modal-card-foot flex items-center g-2">
        <span class="text-7 text-muted grow is-elided">{{ previewDescription }}</span>
        <button type="button" class="btn" @click="handleCancel">Cancel</button>
        <button type="button" class="btn btn-primary" @click="handleSave">
          <span class="icon is-small"><font-awesome-icon icon="check" /></span>
          <span>Save</span>
        </button>
      </footer>
    </div>
  </ModalContainer>
</template>

<script setup lang="ts">
import { computed, watch } from "vue"
import { toast } from "vue-sonner"
import ModalContainer from "@auger/ui/components/ModalContainer.vue"
import {
  describeFilter,
  type FilterFieldDef,
  type SerializedFilter,
  useFilter,
} from "@/composables/useFilter"
import { client } from "@/init/apollo"
import { getTypeSchema } from "@/stores/types"
import FilterConditionsEditor from "./FilterConditionsEditor.vue"

interface Props {
  isOpen: boolean
  modelValue?: SerializedFilter | null
  targetType?: string | null
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  targetType: null,
  title: "Edit filter",
})

const emit = defineEmits<{
  close: []
  "update:modelValue": [value: SerializedFilter]
}>()

// Re-runs when targetType changes. Falls back to [] if the types store
// hasn't hydrated — the empty-state notification in the template covers
// that case for the user.
const fields = computed<FilterFieldDef[]>(() => {
  if (!props.targetType) return []
  const schema = getTypeSchema(props.targetType)
  return (schema?.fields ?? []) as FilterFieldDef[]
})

// Single useFilter instance for the modal's lifetime. State is reset
// (conditions cleared, logic reset to 'and') whenever isOpen flips true,
// then re-loaded from modelValue. We never mutate the parent value —
// save emits serialized; cancel emits close only.
const filters = useFilter(fields, { client, toast })

watch(
  () => props.isOpen,
  (open) => {
    if (!open) return
    filters.clearConditions()
    // Explicit logic reset: load() is additive for partial payloads
    // (it ignores `logic` when absent), so without this the previous
    // open's logic would leak into a freshly-opened filter.
    filters.setLogic(props.modelValue?.logic ?? "and")
    if (!props.modelValue) return
    try {
      filters.load(props.modelValue)
    } catch (err) {
      // A malformed stored filter should be visible, not silently emptied
      // onto the user who'd then overwrite the original on Save. Log loudly
      // and leave the editor in its cleared state; the user can bail out
      // via Cancel.
      console.error("FilterEditorModal: failed to load stored filter", err, props.modelValue)
    }
  },
  { immediate: true }
)

const previewDescription = computed(() => describeFilter(filters.serialized.value, fields.value))

function handleCancel() {
  emit("close")
}

function handleSave() {
  emit("update:modelValue", filters.serialized.value)
  emit("close")
}
</script>

<style>
.filter-editor-card {
  width: min(900px, 90vw);

  .modal-card-body {
    max-height: 70vh;
    min-height: 200px;
  }

  .is-elided {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
